import React, {
    useCallback,
    useMemo,
    useContext,
    useState,
    useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import {
    Button,
    Card,
    StatusLabel,
    Typography,
    makeStyles,
    useMediaQuery,
    CardContent,
} from '@ellucian/react-design-system/core';
import { Icon } from '@ellucian/ds-icons/lib';
import {
    spacing40,
    colorBackgroundDivider,
    spacing30,
    spacing50,
} from '@ellucian/react-design-system/core/styles/tokens';
import { FormattedMessage, useIntl } from 'react-intl';
import { CONSTANTS } from '../Helpers/Configuration';
import forOwn from 'lodash/forOwn';
import GlobalLocaleContext from '../GlobalLocale/GlobalLocaleContext';
import CallToActionDialog from './CallToActionDialog';
import { useApi } from '../ApiContext/';
import { useSnackbar } from '../SnackbarContext/SnackbarContext';
import confetti from 'canvas-confetti';
import { SUPPORTED_FEATURES } from '@nsr/applicant-apply-shared/src/helpers/featureFlags';
import ApplicantCommentDialog from './ApplicantCommentDialog';

const useStyles = makeStyles((theme) => ({
    applicationCardContainer: {
        height: 'fit-content',
        display: 'flex',
        flexDirection: 'row',
        position: 'relative',
    },
    confetti: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none', // To allow interaction with other elements
    },
    completedContainer: {
        flexDirection: 'column',
        alignSelf: 'center',
        textAlign: 'center',
        paddingRight: spacing50,
    },
    infoContainer: {
        flexDirection: 'column',
        paddingLeft: spacing50,
    },
    appStatusIcon:{
        fontWeight: 'bolder'                       
    },
    infoitemLabel: {
        display: 'inline',
        marginRight: spacing40,
    },
    divider: {
        width: 2,
        margin: '6 0',
        background: colorBackgroundDivider,
    },
    statusLabelItems:{
        width:'80px'
    },
    statusFont:{
        color: 'green', 
        fontWeight:'bolder'
    },
    academicContainer: {
        display: 'flex',
        flexDirection: 'row',
        margin: `${spacing30} 0`,
    },
    academicItems: {
        marginRight: spacing50,
    },
    buttonContainer: {
        display: 'flex',
        flex: 'auto',
        justifyContent: 'flex-end',
    },
    button: {
        padding:'0px',
        height: 'fit-content',
        alignSelf: 'center',
    },
    standardField: {
        paddingBottom: spacing30,
    },
    lastAccessed: {
        paddingTop: spacing40,
    },
    buttonMargin:{
        marginTop:'20px'
    },
    submitStatus:{
        fontWeight: 'bolder',
        color:'green' 
    },
    [theme.breakpoints.up('sm')]: {
        button: {
            width: 'fit-content',
            height: 'fit-content',
            marginRight: spacing30,
        },
    },
}));
const ApplicationCard = (props) => {
    const classes = useStyles();
    const { formatMessage } = useIntl();
    const {
        application,
        accessApplication: accessApplicationProp,
        //withdrawApplication: withdrawApplicationProp
    } = props;
    const {
        status,
        name,
        pageStatuses,
        id,
        pageCount,
        defaultLanguage,
        archivedOn,
        academicTerm,
        academicProgram,
    } = application;
    const {featureSupported} = window.PRELOADED_STATE ?? {};
    //we need to make a boolean variable for showing the old submitted UI
    const enableDecision= featureSupported?.[SUPPORTED_FEATURES.applicationReview];
    const paymentStatus = application?.paymentInformation?.paymentStatus;
    const {
        applicationFormStatuses,
        applicationPageStatuses,
        applicationProcessingStatus,
        applicationPaymentInformation,
    } = CONSTANTS;
    const { globalLocale } = useContext(GlobalLocaleContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const canvasRef = useRef(null);
    const api = useApi();
    const snackbar = useSnackbar();
    const [isOngoingApiCall, setIsOngoingApiCall] = useState(false);
    const completedCount = useMemo(() => {
        let count = 0;
        forOwn(pageStatuses, (value) => {
            if (value === applicationPageStatuses.COMPLETED) {
                count++;
            }
        });
        return count;
    }, [pageStatuses]);
    const [isApplicantCommentDialogOpen, setIsApplicantCommentDialogOpen] = useState(false);
    const [applicantComment, setApplicantComment] = useState('');
    const appIsConsideredSubmitted = useMemo(() => {
        //Consider an application submitted if it started but has a pending payment
        if (status === applicationFormStatuses.STARTED) {
            return (
                paymentStatus &&
        ![
            applicationPaymentInformation.paymentStatus.draft,
            applicationPaymentInformation.paymentStatus.successful,
        ].includes(paymentStatus)
            );
        } else {
            return [
                applicationFormStatuses.SUBMITTED,
                applicationProcessingStatus.personCheck,
                applicationProcessingStatus.personError,
                applicationProcessingStatus.pending,
                applicationProcessingStatus.integrationError,
                applicationProcessingStatus.error,
                applicationProcessingStatus.complete,
                applicationProcessingStatus.fraud,
            ].includes(status);
        }
    }, [status, paymentStatus]);

    const getLocalizedName = useCallback(() => {
        if(!name){
            return '';
        }
        return name[globalLocale] ?? name[defaultLanguage];
    }, [globalLocale, name, defaultLanguage]);
    const getActionCardButtonName = useMemo(() => {
        if (appIsConsideredSubmitted || archivedOn) {
            const buttonText = enableDecision ? <FormattedMessage id="applicantHomeReview" /> : <FormattedMessage id="applicantHomeView" />;
            return buttonText;
        }
        
        switch (status) {
            case applicationFormStatuses.NOTSTARTED:
                return <FormattedMessage id="applicantHomeStart" />;
            case applicationFormStatuses.STARTED:
                return <FormattedMessage id="applicantHomeEdit" />;
            case applicationFormStatuses.READONLY:
            case applicationFormStatuses.WITHDRAWN:
                return <FormattedMessage id="applicantHomeView" />;
            default:
                break;
        }
    }, [status, isMobile]);
    
    const onRespondNow = useCallback(() => {
        setIsDialogOpen(true);
    }, [isDialogOpen]);
    
    const onClose = useCallback(() => {
        setIsDialogOpen(false);
    }, [isDialogOpen]);
    
    const triggerConfetti = useCallback(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            confetti.create(canvas, {
                resize: true,
            })({
                particleCount: isMobile ? 150 : 300,
                spread: isMobile ? 70 : 80,
                origin: { y: isMobile ? 1.5 : 3 },
            });
        }
    }, [isMobile, canvasRef]);

    const sendOfferStatus = useCallback(
        async (offerStatus) => {
            try {
                setIsOngoingApiCall(true);
                const response = await api.decisions.updateOfferStatusById(
                    application,
                    offerStatus,
                    applicantComment
                );
                application.offerStatus = response.data.offerStatus;
            } catch (e) {
                console.error(e);
                const error = await e;
                if (error.errorCode === 422) {
                    snackbar.error(
                        formatMessage({
                            id: 'applicantHomeSendOfferStatusWhenDecisionStatusChangedError',
                        })
                    );
                } else {
                    snackbar.error(
                        formatMessage({ id: 'applicantHomeSendOfferStatusError' })
                    );
                }
            } finally {
                setIsOngoingApiCall(false);
                onClose();
            }
        },
        [api, application, snackbar, onClose, applicantComment]
    );

    const onAccept = useCallback(async () => {
        await sendOfferStatus(CONSTANTS.applicationOfferStatus.accepted);
        //trigger confetti
        if (
            application.decisionStatus ===
        CONSTANTS.applicationDecisionStatus.admitted &&
        application.offerStatus === CONSTANTS.applicationOfferStatus.accepted
        ) {
            triggerConfetti();
        }
    }, [
        sendOfferStatus,
        triggerConfetti,
        application.decisionStatus,
        application.offerStatus,
    ]);

    const onDecline = useCallback(() => {
        setIsApplicantCommentDialogOpen(true);
    }, []);

    const onDeclineConfirmation = useCallback(async () => {
        await sendOfferStatus(CONSTANTS.applicationOfferStatus.declined);
        setIsApplicantCommentDialogOpen(false);
        setIsDialogOpen(false);
    }, [sendOfferStatus]);

    const onConditionsMet = useCallback(async () => {
        await sendOfferStatus(CONSTANTS.applicationOfferStatus.conditionsCompleted);
        setIsDialogOpen(false);
        snackbar.success(formatMessage({ id: 'applicantHomeConditionsMetSuccess' }));
    }, [sendOfferStatus]);

    const accessApplication = useCallback(
        () => accessApplicationProp(application),
        [accessApplicationProp, id, status]
    );
    //This is a data structure for designing the decision status attributes used in the application card
    const decisionStatusAttributes = {
        [CONSTANTS.applicationDecisionStatus.admitted]: {
            iconName: 'graduation',
            color: '#008000',
            textKey: 'applicantHomeActionButtonAcceptOrRejectOffer',
            acceptedTextKey: 'applicantHomeOfferAccepted',
            declinedTextKey: 'applicantHomeOfferDeclined',
            statusLabelKey: 'applicantHomeDecisionAdmitted',
        },
        [CONSTANTS.applicationDecisionStatus.conditionalAdmit]: {
            iconName: 'list-view',
            color: '#8A6A00',
            textKey: 'applicantHomeActionButtonViewConditionDetails',
            acceptedTextKey: 'applicantHomeConditionsAccepted',
            statusLabelKey: 'applicantHomeDecisionConditionalAdmit',
        },
        [CONSTANTS.applicationDecisionStatus.waitlist]: {
            iconName: 'clock',
            color: '#8A6A00',
            textKey: 'applicantHomeActionButtonAcceptOrRejectWaitlist',
            acceptedTextKey: 'applicantHomeWaitlistAccepted',
            declinedTextKey: 'applicantHomeWaitlistDeclined',
            statusLabelKey: 'applicantHomeDecisionWaitlist',
        },
        [CONSTANTS.applicationDecisionStatus.declined]: {
            iconName: 'comments',
            color: '#808080',
            statusLabelKey: 'applicantHomeDecisionDeclined',
        },
    };
    return (
        <Card>
            <CardContent className={classes.applicationCardContainer}>
                <canvas ref={canvasRef} className={classes.confetti} />
                <div className={classes.completedContainer}>
                    <Typography variant="h1">
                        {application.decisionStatus ? (
                            <div className={classes.statusLabelItems}>
                                <Typography className={classes.iconFontWeight}>
                              
                                    <Icon
                                        name={
                                            decisionStatusAttributes[application.decisionStatus] &&
                    decisionStatusAttributes[application.decisionStatus]
                      .iconName
                                        }
                                        style={{
                                            color:
                      decisionStatusAttributes[application.decisionStatus]
                        .color,
                                            fontWeight:'bolder'
                                        }}
                                        large
                                    />
                                </Typography>
                            </div>
                           
                        ) : appIsConsideredSubmitted && enableDecision ? (
                            <div>
                                <Icon large name="check-feedback" className={classes.statusFont} />
                            </div>
                        ) : (
                            <> 
                                <FormattedMessage
                                    id="applicantHomePagesCompleted"
                                    values={{
                                        currentPage: completedCount.toLocaleString(globalLocale),
                                        totalPages: pageCount.toLocaleString(globalLocale),
                                    }}
                                />
                            </>
                        )}
                    </Typography>
                    <div className={classes.statusLabelItems}>
                        <Typography variant="body3">
                            {application.decisionStatus ? (
                                <Typography
                                    style={{
                                        color: decisionStatusAttributes[application?.decisionStatus].color,
                                        fontWeight:'bolder',
                                    }}
                                >
                                    {formatMessage({
                                        id: decisionStatusAttributes[application?.decisionStatus]?.statusLabelKey
                                    })}
                                </Typography>
                            ) : appIsConsideredSubmitted && enableDecision ? (
                                <Typography className={classes.submitStatus}>
                                    <FormattedMessage id="applicantHomeSubmitted" />  
                                </Typography>
                            ) : (
                                <div>
                                    <FormattedMessage id="applicantHomePages" />
                                    <Typography variant="body3"><FormattedMessage id="applicantHomeCompleted" /></Typography>
                                </div>
                            )}
                        </Typography>
                    </div>
                </div>
                <div className={classes.divider} />
                <div className={classes.infoContainer}>
                    <Typography
                        variant={isMobile ? 'h4' : 'h3'}
                        className={classes.standardField}
                    >
                        {getLocalizedName()}
                    </Typography>
                    <div className={classes.academicContainer}>
                        {academicTerm && (
                            <Typography variant="body2" className={classes.academicItems}>
                                {academicTerm}
                            </Typography>
                        )}
                        {academicProgram && (
                            <Typography variant="body2" className={classes.academicItems}>
                                {academicProgram}
                            </Typography>
                        )}
                    </div>
                    {/*If decision is not enabled and app is not yet submitted we need to show the status format message.*/}
                    {(!enableDecision || !appIsConsideredSubmitted) && <Typography variant="body3" className={classes.infoitemLabel}>
                        <FormattedMessage id="applicantHomeStatus" />
                    </Typography>}
                    {!archivedOn && (
                        <>
                            {status === applicationFormStatuses.NOTSTARTED &&
                            <StatusLabel text={formatMessage({ id: 'applicantHomeNotStarted' })} type="draft" />
                            }
                            {/*if the decision is not enabled and app is submitted we want to show the submitted status chip*/}
                            {!enableDecision && appIsConsideredSubmitted ?
                                <StatusLabel text={formatMessage({ id: 'appDesignResponsesSubmitted' })} type="success" /> 
                                : <></>
                            }
                            {status === applicationFormStatuses.STARTED && (
                                <StatusLabel
                                    text={
                                        paymentStatus &&
                                            ![
                                                applicationPaymentInformation.paymentStatus.draft,
                                                applicationPaymentInformation.paymentStatus.successful,
                                            ].includes(paymentStatus)
                                            ? formatMessage({ id: 'applicantHomePaymentPending' })
                                            : formatMessage({ id: 'applicantHomeInProgress' })
                                    }
                                    type="pending"
                                />
                            )}
                            {status === applicationFormStatuses.READONLY && (
                                <StatusLabel
                                    text={formatMessage({ id: 'applicantHomeReadonly' })}
                                />
                            )}
                            {status === applicationFormStatuses.WITHDRAWN && (
                                <StatusLabel
                                    text={formatMessage({ id: 'applicantHomeWithdrawn' })}
                                />
                            )}
                            { application?.offerStatus === CONSTANTS.applicationOfferStatus.conditionsCompleted || (application.decisionStatus &&
                                application.decisionStatus !==
                                    CONSTANTS.applicationDecisionStatus.declined && !application?.offerStatus) ? (
                                    <Button
                                        color="secondary"
                                        className={classes.buttonMargin}
                                        onClick={onRespondNow}
                                    >
                                        {formatMessage({ id: decisionStatusAttributes[application?.decisionStatus]?.textKey })}                                    
                                    </Button>
                                ) : (
                                    <></>
                                )}
                            {application.offerStatus &&
                                <Typography variant="h6" color="textSecondary">
                                    {application.offerStatus === CONSTANTS.applicationOfferStatus.accepted || application.offerStatus === CONSTANTS.applicationOfferStatus.conditionsCompleted
                                        ? formatMessage({ id: decisionStatusAttributes[application.decisionStatus]?.acceptedTextKey })
                                        : formatMessage({ id: decisionStatusAttributes[application.decisionStatus]?.declinedTextKey})
                                    }
                                </Typography>}
                            {isDialogOpen && (
                                <CallToActionDialog
                                    application={application}
                                    onClose={onClose}
                                    onAccept={onAccept}
                                    onDecline={onDecline}
                                    onConditionsMet={onConditionsMet}
                                    isOngoingApiCall={isOngoingApiCall}
                                    applicantComment={applicantComment}
                                    setApplicantComment={setApplicantComment}
                                />
                            )}
                            {isApplicantCommentDialogOpen && (
                                <ApplicantCommentDialog
                                    application={application}
                                    onClose={()=>setIsApplicantCommentDialogOpen(false)}
                                    onDeclineConfirmation={onDeclineConfirmation}
                                    isOngoingApiCall={isOngoingApiCall}
                                    applicantComment={applicantComment}
                                    setApplicantComment={setApplicantComment}
                                />
                            )}
                        </>
                    )}
                    {isMobile && (
                        <Button
                            className={classes.button}
                            fluid
                            onClick={accessApplication}
                            variant={enableDecision && application.decisionStatus ? 'text' : undefined}
                            color={enableDecision && !application.decisionStatus && appIsConsideredSubmitted ? 'secondary' : 'primary'}
                        >
                            {getActionCardButtonName}
                        </Button>
                    )}
                </div>

                {!isMobile && (
                    <div className={classes.buttonContainer}>
                        <Button
                            className={classes.button}
                            onClick={accessApplication}
                            variant={enableDecision && application.decisionStatus ? 'text' : undefined}
                            color={enableDecision && !application.decisionStatus && appIsConsideredSubmitted ? 'secondary' : 'primary'}
                        >
                            {getActionCardButtonName}
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
ApplicationCard.propTypes = {
    application: PropTypes.object,
    accessApplication: PropTypes.func,
    withdrawApplication: PropTypes.func,
};
export default ApplicationCard;
